import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Button, Modal } from 'antd';

import AppPageHeader from '@/components/_common/AppPageHeader';
import AccountsCostCentersGeneralModalForm from '@/components/Financial/Accounts/CostCenters/General/ModalForm';
import { PageProps } from '@/types/PageProps';
import CostCenterTable from '@/components/Financial/Accounts/CostCenters/General/CostCenterTable';
import {
  CostCenter,
  ListCostCentersQuery,
} from '@/core/domain/enterprise/CostCenter/CostCenter.types';
import { DefaultRouteTabs } from '@/components/_common';
import { useDebounce, useDisclosure } from '@/hooks';
import {
  useCostCenterCreate,
  useCostCenterDelete,
  useCostCenterEdit,
} from '@/core/domain/enterprise/CostCenter/CostCenter.hooks';
import TableExtraFilters from '@/components/Financial/Accounts/CostCenters/General/CostCenterTable/TableExtraFilters';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const CostCentersPage = ({ route }: PageProps) => {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<ListCostCentersQuery>({
    order: 'DESC',
    ...convertParams<ListCostCentersQuery>({
      order: 'string',
      description: 'string',
      active: 'boolean',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const [selectedCostCenter, setSelectedCostCenter] = useState<CostCenter>();
  const [modalGeneralVisible, modalGeneralVisibleAction] = useDisclosure();

  const costCenterCreate = useCostCenterCreate();
  const costCenterEdit = useCostCenterEdit();
  const costCenterRemove = useCostCenterDelete();

  const handleSaveCostCenter = async (value: CostCenter) => {
    try {
      if (selectedCostCenter) {
        costCenterEdit.mutateAsync({ ...selectedCostCenter, ...value });
      } else {
        await costCenterCreate.mutateAsync(value);
      }
    } catch {
    } finally {
      modalGeneralVisibleAction.close();
    }
  };

  const showConfirmRemoveCostCenter = (costCenter: CostCenter) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'costCenters.contacts.remove.modal.title',
      }),
      content: intl.formatMessage({
        id: 'costCenters.contacts.remove.modal.content',
      }),
      onOk: async () => {
        try {
          await costCenterRemove.mutateAsync(costCenter.id);
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'costCenters.contacts.remove.modal.okText',
      }),
      okType: 'danger',
    });
  };

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      persistSearchParams={false}
      route={route.parent}
      extra={
        <Button
          type="primary"
          block
          onClick={modalGeneralVisibleAction.open}
          disabled={!hasPermissions([Authorities.REGISTER_COST_CENTER_ADD])}
        >
          <FormattedMessage id="costCenters.create" />
        </Button>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, description: value }))
        }
        searchValue={queryParams.description}
        extraFilters={
          <TableExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <CostCenterTable
        setSelected={setSelectedCostCenter}
        toggleModal={modalGeneralVisibleAction.toggle}
        showRemove={showConfirmRemoveCostCenter}
        queryParams={queryDebounce}
      />

      <AccountsCostCentersGeneralModalForm
        visible={modalGeneralVisible}
        costCenter={selectedCostCenter}
        loading={costCenterCreate.isLoading || costCenterEdit.isLoading}
        onSubmit={handleSaveCostCenter}
        onCancel={() => {
          setSelectedCostCenter(undefined);
          modalGeneralVisibleAction.close();
        }}
      />
    </DefaultRouteTabs>
  );
};

export default CostCentersPage;
